import { cloneElement, h, toChildArray } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { INITIAL_FOCUS_SELECTOR, LAST_FOCUSED_ELEMENT_EXCEPTIONS } from '../../../../core/accessibility/constants';
import { NO_FOCUS_RING } from '../../../../core/shortcut/types';
import { UI_ACCESSIBILITY_VISIBILITY, UI_VISIBLE } from '../../../../store/types';
import { USER_SET_FOCUS } from '../../../../types';
import { elementIsWithin, systemInfo, VOID_FN } from '../../../../utils';
import { computeVideoTitle } from '../../../../utils/videoMetaUtils';
import { useDialogContext } from '../../../common/context/DialogContext';
import { usePlayerContext, useStateRef } from '../../../common/hooks';
import { isValidateKeyboard } from '../../../common/utils/keyboard';
import { useRegisterKeyboardTracker } from '../../../common/utils/keyboardTracker';
import { connect, withBreakPoints, withError } from '../../hoc';

const HIDE_DELAY = 3000;

function InteractionHandler({
  children,
  style = { display: 'flex', flexDirection: 'row', height: '100%' },
  ...props
}) {
  const {
    displayUiAccessibility, forceHidden, forceVisible, canInteract, isInteracting, waitStart, /* UI store interactions */
    loaded, mobile, zappingEnabled, /* Media properties */
    isPlaying, isSeeking, isFullscreen, isAd, /* Store updates which trigger UI display when manipulating player API directly */
    liveLabel, pubLabel,
    videoTitle, subTitle, videoPreTitle, videoAdditionalTitle,
    isTv,
    focusOnStartScreen
  } = props;
  useRegisterKeyboardTracker();

  const showProps = [canInteract, isInteracting, forceHidden, zappingEnabled, isPlaying, isSeeking, isFullscreen];

  const player = usePlayerContext();
  const container = useRef(null);
  const timerRef = useRef(null); /* autohide timer */
  const [visible, setVisible, visibility] = useStateRef(false);
  const [ariaLive, setAriaLive] = useState('off');
  const nonInitRenderRef = useRef(false);

  const { computedTitle, computedSubtitle } = useMemo(
    () => {
      const { computedTitle: title, computedSubtitle: sTitle } = computeVideoTitle(videoTitle, subTitle, videoPreTitle, videoAdditionalTitle);
      return ({
        computedTitle: title,
        computedSubtitle: sTitle.replace(/(S|E)(\d+)/gi, (_, character, numberMatch) => (
          `${['s', 'S'].includes(character) ? 'Saison' : 'Épisode'} ${numberMatch}`
        ))
      });
    },
    [videoTitle, subTitle, videoPreTitle, videoAdditionalTitle]
  );

  /* .replace twice only take ~ .005ms to process */

  const playerMetaVocalization = useMemo(
    () => (isAd
      ? (!systemInfo.isMac ? pubLabel.replace(/sec/, 'secondes') : pubLabel && 'Publicité').trim()
      : `${liveLabel.replace(/h/, 'heure').replace(/min/, 'minutes')} ${computedTitle} ${computedSubtitle}`.trim()),
    [pubLabel, liveLabel, computedTitle, computedSubtitle]
  );

  /* Dialog context */
  const dialogCtx = useDialogContext();
  const closeDialogFirst = (fn) => () => (dialogCtx.dialogOpened ? dialogCtx.closeAll() : fn());

  // Detect pointerDown for focus triggered with click
  useEffect(() => {
    const handlePointerDown = () => {
      // Whenever user clicks anywhere, we should hide accessibility visuals
      player.store.dispatch({ type: UI_ACCESSIBILITY_VISIBILITY, payload: { displayUiAccessibility: false } });
    };

    if (document) {
      document.addEventListener('pointerdown', handlePointerDown);
    }

    return () => {
      if (document) {
        document.removeEventListener('pointerdown', handlePointerDown);
      }
    };
  });

  const lastFocusedElement = useRef(null);
  useEffect(() => {
    // Reset lastFocusedElement when UI is hidden, because then the ref to the element is lost forever
    if (!visible) {
      lastFocusedElement.current = null;
    }
  }, [visible]);

  // React.Element.onFocus <=> 'focusin' event
  const onFocus = (e) => {
    const focusedElementIsInContainer = !!e.target && container.current.contains(e.target);

    player.focused$.next(true);

    if (!visible || !focusedElementIsInContainer || !e.target || LAST_FOCUSED_ELEMENT_EXCEPTIONS.some((selector) => e.target.matches(selector))) {
      return;
    }

    lastFocusedElement.current = e.target;
  };

  const handleBlurOutsideContainer = () => {
    const currentContainer = container.current;
    // `setTimeout` here to fix some cases where document.activeElement is not updated yet
    // since `onBlur` fires before focus has fully shifted
    setTimeout(() => {
      const nextFocusedElementOutsideContainer = currentContainer && !currentContainer.contains(document.activeElement);
      if (!nextFocusedElementOutsideContainer) {
        return;
      }

      player.store.dispatch({ type: UI_ACCESSIBILITY_VISIBILITY, payload: { displayUiAccessibility: false } });

      // If next focused element is outside container
      // And player controls are still visible, this means the user triggered the blur
      if (visible) {
        lastFocusedElement.current = null;
        return;
      }

      // Otherwise, if `visible = false` this means it's the autoHide() that triggered the blur
      // In this case, we should put the focus on the player container to let user keyboard navigate back into the player
      if (!isFullscreen && !isTv && !waitStart) {
        container.current?.focus({ preventScroll: true });
        // We can hide the accessibility visuals, since it will be visible again on keyboard navigation
        player.store.dispatch({ type: UI_ACCESSIBILITY_VISIBILITY, payload: { displayUiAccessibility: false } });
      }
    }, 0);
  };

  const onBlur = (e) => {
    handleBlurOutsideContainer(e);

    // Prevent closing dialogs if the focus goes to a child
    if (elementIsWithin(e.target, e.relatedTarget)) {
      return;
    }

    const playerContainsNextFocusedElement = e.relatedTarget && container.current && container.current.contains(e.relatedTarget);

    // If relatedTarget is null, this means the next focused element is not rendered.
    // This can happen when focusing on animated elements (such as SlideMenu in Settings, etc).
    // In that case we don't want to close dialogs, since the focus is probably made inside the current opened dialog
    if (e.relatedTarget === null || playerContainsNextFocusedElement) {
      return;
    }

    if (dialogCtx.dialogOpened) {
      dialogCtx.closeAll();
    }
  };

  const canShow = useCallback(
    () => (loaded && canInteract && !forceHidden && !waitStart),
    [loaded, canInteract, forceHidden, waitStart]
  );

  const canHide = useCallback(
    () => (
      forceHidden
      || ((!forceVisible && !isInteracting) && (isPlaying || !loaded))
      || forceVisible
    ),
    [forceHidden, isInteracting, isPlaying, loaded, forceVisible]
  );

  const shouldAutoHide = useCallback(() => visible && !isInteracting && isPlaying, [visible, isPlaying, isInteracting]);
  const shouldAutoShow = useCallback(
    () => (canInteract || isInteracting) && showProps.some(Boolean),
    [canInteract, isInteracting, ...showProps]
  );

  const hide = () => canHide() && setVisible(false);
  const show = () => canShow() && setVisible(true);

  const resetTimer = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const autoHide = (delay) => {
    resetTimer(); /* clear timer to avoid multiple hide requests */
    timerRef.current = setTimeout(hide, delay);
  };

  const handleClick = () => (!isInteracting && Promise.resolve(player.handleClickEvent()));
  const handleTap = () => (visibility.current ? hide() : show());
  const handleMouseLeave = () => (!forceHidden && !forceVisible) && autoHide(50);
  const handleEscapeVolumeDialog = (e) => {
    if (dialogCtx.dialogOpened || isFullscreen) {
      return;
    }

    // Handle the volume dialog case, which is a "over" dialog, not stored in dialogContext
    const isActiveElementInVolumeDialog = document.activeElement.getAttribute('name') === 'slider-volume';
    if (isActiveElementInVolumeDialog) {
      e.preventDefault();
      e.stopPropagation();

      player.userEvents$.next({
        action: USER_SET_FOCUS,
        payload: { element: document.querySelector('[name="btn-volume"]') }
      });
    }
  };

  useEffect(() => {
    // Focus only of autostart=true (aka !waitStart) OR autostart=false and focusOnStartScreen=true
    let timeout;
    if (container.current && (!waitStart || focusOnStartScreen)) {
      timeout = setTimeout(() => {
        container.current.querySelector(INITIAL_FOCUS_SELECTOR)?.focus({ preventScroll: true });
      }, 300);
    }
    return () => { clearTimeout(timeout); };
  }, [waitStart, focusOnStartScreen, visible]);

  const isFirstTab = useRef(true);
  useEffect(() => { if (!visible) isFirstTab.current = true; }, [visible]);

  /**
   * When the UI is hidden, or the focus is on the container / previous element, we need to prevent the "Tab" keyboard event
   * to force the focus on the INITIAL_FOCUS_SELECTOR element in the player when using keyboard navigation
   */
  const handleTab = (e) => {
    if (e.shiftKey) {
      return;
    }

    if (isFirstTab.current || document.activeElement.matches('.magneto-wrapper')) {
      e.preventDefault();
      e.stopPropagation();

      if (!visibility.current) {
        show();
      }
      isFirstTab.current = false;
      document.querySelector(INITIAL_FOCUS_SELECTOR)?.focus({ preventScroll: true });
    }
  };

  const handleKeyEvent = (e) => {
    if (e.keyCode !== 9 /* TAB */) {
      e.preventDefault();
    } else {
      handleTab(e);
    }

    player.handleKeyEvent(e.keyCode, { shiftKey: e.shiftKey });

    if (e.keyCode === 27 /* ESC */) {
      handleEscapeVolumeDialog(e);
      dialogCtx.closeAll();
    }

    // ENTER & SPACE keys
    if (isValidateKeyboard(e)) {
      player.store.dispatch({ type: UI_ACCESSIBILITY_VISIBILITY, payload: { displayUiAccessibility: true } });
    }
  };

  useEffect(() => player.setInteractionLayer(container.current), []);
  useEffect(() => forceHidden && hide(), [forceHidden]);
  useEffect(() => forceVisible && show(), [forceVisible]);
  useEffect(() => shouldAutoShow() && show(), showProps);
  useEffect(() => !loaded && setVisible(false), [loaded]);
  useEffect(() => {
    if (isInteracting || !isPlaying || !visible || forceVisible) resetTimer();
    if (shouldAutoHide()) {
      autoHide(HIDE_DELAY);
    }
  }, [visible, isInteracting, isPlaying, forceVisible]);

  useEffect(() => {
    if (displayUiAccessibility) {
      show();
    }
  }, [displayUiAccessibility]);
  useEffect(() => displayUiAccessibility && autoHide(HIDE_DELAY), [displayUiAccessibility]);

  useEffect(() => {
    player.store.dispatch({ type: UI_VISIBLE, payload: { hasUiVisible: visible } });
    player.trigger(UI_VISIBLE, { visible });
  }, [visible]);

  useEffect(() => {
    /** We set `aria-live` to polite whenever the value of `videoMeta`
     *  changes so the screen reader like Nvda detect changes and tells
     *  the user the lastly updated field.
     *  Ignore first render, we don't want `aria-live` to be 'polite'
     *  so the user won't be overwhelmed by screen reader whenever the
     *  `adMeta` is updated. The value of `isAd` take time to be updated
     *  compared to the value of `videoMeta`. (see dependency array of the
     *  useEffect)
     *  * */
    if (nonInitRenderRef.current) {
      if (!isAd) {
        setAriaLive('polite');
        setTimeout(() => {
          setAriaLive('off');
        }, 300);
      }
    } else {
      nonInitRenderRef.current = true;
    }
  }, [isAd, liveLabel, pubLabel, computedSubtitle, computedTitle]);

  const className = useMemo(() => `ftv-in-customizable magneto-wrapper ${isFullscreen || !displayUiAccessibility ? NO_FOCUS_RING : ''}`.trim(), [isFullscreen, displayUiAccessibility]);

  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
    <div
      style={style}
      onClick={closeDialogFirst(mobile ? handleTap : handleClick)}
      onMouseLeave={mobile ? VOID_FN : handleMouseLeave}
      onMouseMove={mobile ? VOID_FN : show}
      onKeyDown={mobile ? VOID_FN : handleKeyEvent}
      role="region"
      className={className}
      ref={container}
      onFocus={onFocus}
      tabIndex="0" /* eslint-disable-line */
      onBlur={onBlur}
      name="ftv-magneto-player"
      aria-label={playerMetaVocalization}
      aria-live={ariaLive}
    >
      {
        toChildArray(children)
          .map((child) => cloneElement(child, {
            ...child.props,
            UIVisible: visible,
            setUIVisibility: (val) => setVisible(val),
            handleTap
          }))
      }
    </div>
  );
}

const selector = ({
  playback: { isPlaying },
  zapping: { list, zappingEnabled = list.length > 0 },
  media: { isAd, isLive, isDVR, isTv, loaded,
    videoTitle,
    subTitle,
    videoPreTitle,
    videoAdditionalTitle
  },
  ui: {
    canInteract, forceVisible, forceHidden, hoverTimeline,
    isFullscreen, isInteracting, isSeeking, waitStart,
    displayUiAccessibility, liveLabel, pubLabel,
    focusOnStartScreen
  }
}) => ({
  forceHidden, forceVisible, canInteract, isInteracting, hoverTimeline,
  waitStart, loaded, isAd, isLive, isDVR, isTv, zappingEnabled,
  isPlaying, isSeeking, isFullscreen, displayUiAccessibility, liveLabel,
  pubLabel,
  videoTitle,
  subTitle,
  videoPreTitle,
  videoAdditionalTitle,
  focusOnStartScreen
});

export default withError(withBreakPoints(connect(selector)(InteractionHandler)));
